/**
 * Grid Coordinate System for Floor Plan Pin Placement
 *
 * This module provides a grid-based coordinate system that ensures
 * pixel-perfect pin alignment between full and zoomed minimaps.
 *
 * Key Principle: Both minimaps use the same grid lookup, eliminating
 * floating-point drift from percentage-based calculations.
 *
 * Numerical-correctness note: this file is consumed by the watermark pin
 * pipeline. The formulas below are load-bearing for export accuracy. Guards
 * added here only protect against degenerate inputs (zero/NaN divisions or
 * dimensions); they never alter results for valid inputs.
 */

import { logger } from './logger';

// ============================================================================
// Types
// ============================================================================

/**
 * Position within the grid system
 * cellX/cellY: Which grid cell (0 to divisions-1)
 * offsetX/offsetY: Position within that cell (0-1)
 */
export interface GridPosition {
  cellX: number;
  cellY: number;
  offsetX: number;
  offsetY: number;
}

/**
 * Grid system configuration
 */
export interface GridSystem {
  divisions: number;      // Number of grid divisions (e.g., 12, 24)
  imageWidth: number;     // Source image width in pixels
  imageHeight: number;    // Source image height in pixels
  cellWidth: number;      // Calculated: imageWidth / divisions
  cellHeight: number;     // Calculated: imageHeight / divisions
}

/**
 * Rectangle definition for viewports and destinations
 */
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Viewport for rendering (source region of image)
 */
export interface Viewport {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Floor plan metadata including grid information
 */
export interface FloorPlanGridMetadata {
  originalWidth: number;
  originalHeight: number;
  cropRegion?: Rect;
  gridDivisions: number;
  aspectRatio: number;
}

/**
 * 2D point in pixel/percentage space, returned by the conversion helpers.
 */
export interface Point2D {
  x: number;
  y: number;
}

// ============================================================================
// Constants
// ============================================================================

export const DEFAULT_GRID_DIVISIONS = 24; // 24x24 grid for precision
export const MIN_GRID_DIVISIONS = 6;
export const MAX_GRID_DIVISIONS = 48;

// ============================================================================
// Internal helpers (no behavior change on valid inputs)
// ============================================================================

/**
 * Resolve a divisions argument to a safe, positive integer-ish divisor.
 *
 * Valid inputs (finite, >= 1) are returned unchanged so the math is identical.
 * Only NaN / non-finite / <= 0 values are coerced to DEFAULT_GRID_DIVISIONS to
 * prevent divide-by-zero and NaN propagation into pin coordinates.
 */
function safeDivisions(divisions: number): number {
  return Number.isFinite(divisions) && divisions > 0
    ? divisions
    : DEFAULT_GRID_DIVISIONS;
}

/**
 * Guard a divisor used in a denominator. Returns the value when it is a finite
 * non-zero number, otherwise 1 so the division yields a defined (not NaN/Inf)
 * result. Used only for image/viewport/target dimensions that should never be
 * zero in practice.
 */
function safeDivisor(value: number): number {
  return Number.isFinite(value) && value !== 0 ? value : 1;
}

// ============================================================================
// Grid System Creation
// ============================================================================

/**
 * Create a grid system for a floor plan image
 */
export function createGridSystem(
  imageWidth: number,
  imageHeight: number,
  divisions: number = DEFAULT_GRID_DIVISIONS
): GridSystem {
  const clampedDivisions = Math.max(MIN_GRID_DIVISIONS, Math.min(MAX_GRID_DIVISIONS, divisions));

  return {
    divisions: clampedDivisions,
    imageWidth,
    imageHeight,
    cellWidth: imageWidth / clampedDivisions,
    cellHeight: imageHeight / clampedDivisions
  };
}

// ============================================================================
// Coordinate Conversions
// ============================================================================

/**
 * Convert percentage coordinates (0-100) to grid position
 */
export function percentToGrid(
  xPercent: number,
  yPercent: number,
  divisions: number = DEFAULT_GRID_DIVISIONS
): GridPosition {
  const safeDiv = safeDivisions(divisions);

  // Clamp percentages to valid range
  const x = Math.max(0, Math.min(100, xPercent));
  const y = Math.max(0, Math.min(100, yPercent));

  // Convert to 0-divisions range
  const gridX = (x / 100) * safeDiv;
  const gridY = (y / 100) * safeDiv;

  // Extract cell and offset
  const cellX = Math.floor(gridX);
  const cellY = Math.floor(gridY);
  const offsetX = gridX - cellX;
  const offsetY = gridY - cellY;

  // Handle edge case where percentage is exactly 100
  const finalCellX = Math.min(cellX, safeDiv - 1);
  const finalCellY = Math.min(cellY, safeDiv - 1);
  const finalOffsetX = cellX >= safeDiv ? 1 : offsetX;
  const finalOffsetY = cellY >= safeDiv ? 1 : offsetY;

  return {
    cellX: finalCellX,
    cellY: finalCellY,
    offsetX: finalOffsetX,
    offsetY: finalOffsetY
  };
}

/**
 * Convert grid position back to percentage coordinates (0-100)
 * Used for backward compatibility
 */
export function gridToPercent(
  gridPos: GridPosition,
  divisions: number = DEFAULT_GRID_DIVISIONS
): Point2D {
  const safeDiv = safeDivisions(divisions);

  const x = ((gridPos.cellX + gridPos.offsetX) / safeDiv) * 100;
  const y = ((gridPos.cellY + gridPos.offsetY) / safeDiv) * 100;

  return {
    x: Math.max(0, Math.min(100, x)),
    y: Math.max(0, Math.min(100, y))
  };
}

/**
 * Convert grid position to pixel coordinates within a target rectangle
 * This is the key function that ensures alignment - both minimaps use this
 */
export function gridToPixel(
  gridPos: GridPosition,
  targetWidth: number,
  targetHeight: number,
  divisions: number = DEFAULT_GRID_DIVISIONS
): Point2D {
  const safeDiv = safeDivisions(divisions);

  // Calculate the fractional position (0-1) from grid
  const fracX = (gridPos.cellX + gridPos.offsetX) / safeDiv;
  const fracY = (gridPos.cellY + gridPos.offsetY) / safeDiv;

  // Convert to pixels within target
  const x = fracX * targetWidth;
  const y = fracY * targetHeight;

  return { x, y };
}

/**
 * Convert pixel coordinates to grid position
 */
export function pixelToGrid(
  pixelX: number,
  pixelY: number,
  imageWidth: number,
  imageHeight: number,
  divisions: number = DEFAULT_GRID_DIVISIONS
): GridPosition {
  // Convert to percentage first (guard against zero-sized images)
  const xPercent = (pixelX / safeDivisor(imageWidth)) * 100;
  const yPercent = (pixelY / safeDivisor(imageHeight)) * 100;

  return percentToGrid(xPercent, yPercent, divisions);
}

// ============================================================================
// Zoom Region Calculations
// ============================================================================

/**
 * Calculate the zoom viewport based on grid position
 * Returns the source region to display in the zoomed minimap
 *
 * Key insight: Use same number of cells in X and Y directions.
 * Since cells already have the image's aspect ratio, this automatically
 * produces a viewport with the correct aspect ratio.
 */
export function getZoomViewport(
  gridPos: GridPosition,
  gridSystem: GridSystem,
  zoomLevel: number = 4,
  _targetAspectRatio?: number  // Kept for API compatibility but not used
): Viewport {
  void _targetAspectRatio; // Suppress unused warning
  const { divisions, cellWidth, cellHeight } = gridSystem;

  // Calculate how many cells to show based on zoom level
  // Use same number of cells in X and Y - this preserves aspect ratio
  // because cells themselves have the image's aspect ratio.
  // Guard the zoomLevel divisor so a zero/NaN level cannot yield NaN cells.
  const cellsToShow = Math.max(2, Math.ceil(divisions / safeDivisor(zoomLevel)));

  // Calculate the center in fractional cell coordinates
  const centerCellX = gridPos.cellX + gridPos.offsetX;
  const centerCellY = gridPos.cellY + gridPos.offsetY;

  // Center the viewport on the pin position
  let startCellX = centerCellX - cellsToShow / 2;
  let startCellY = centerCellY - cellsToShow / 2;
  let endCellX = startCellX + cellsToShow;
  let endCellY = startCellY + cellsToShow;

  // Clamp to image boundaries (shift viewport to keep size constant)
  if (startCellX < 0) {
    endCellX -= startCellX;  // Shift right
    startCellX = 0;
  }
  if (startCellY < 0) {
    endCellY -= startCellY;  // Shift down
    startCellY = 0;
  }
  if (endCellX > divisions) {
    startCellX -= (endCellX - divisions);  // Shift left
    endCellX = divisions;
  }
  if (endCellY > divisions) {
    startCellY -= (endCellY - divisions);  // Shift up
    endCellY = divisions;
  }

  // Final safety clamp
  startCellX = Math.max(0, startCellX);
  startCellY = Math.max(0, startCellY);
  endCellX = Math.min(divisions, endCellX);
  endCellY = Math.min(divisions, endCellY);

  // Convert cell coordinates to pixel coordinates
  const x = startCellX * cellWidth;
  const y = startCellY * cellHeight;
  const width = (endCellX - startCellX) * cellWidth;
  const height = (endCellY - startCellY) * cellHeight;

  logger.debug('getZoomViewport calculated', {
    gridPos: `(${gridPos.cellX}+${gridPos.offsetX.toFixed(2)}, ${gridPos.cellY}+${gridPos.offsetY.toFixed(2)})`,
    cellsToShow,
    startCell: `(${startCellX.toFixed(2)}, ${startCellY.toFixed(2)})`,
    endCell: `(${endCellX.toFixed(2)}, ${endCellY.toFixed(2)})`,
    viewport: `${x.toFixed(0)},${y.toFixed(0)} ${width.toFixed(0)}x${height.toFixed(0)}`
  });

  return { x, y, width, height };
}

/**
 * Calculate pin position within a viewport
 * This ensures the pin appears at the same relative position in any viewport
 */
export function getPinPositionInViewport(
  gridPos: GridPosition,
  viewport: Viewport,
  destRect: Rect,
  gridSystem: GridSystem
): Point2D {
  const { cellWidth, cellHeight } = gridSystem;

  // Calculate pin's absolute position in source image
  const pinSourceX = (gridPos.cellX + gridPos.offsetX) * cellWidth;
  const pinSourceY = (gridPos.cellY + gridPos.offsetY) * cellHeight;

  // Calculate pin's position relative to viewport (0-1)
  // Guard viewport dimensions so a zero-sized viewport cannot produce NaN/Inf.
  const relativeX = (pinSourceX - viewport.x) / safeDivisor(viewport.width);
  const relativeY = (pinSourceY - viewport.y) / safeDivisor(viewport.height);

  // Convert to destination coordinates
  const x = destRect.x + (relativeX * destRect.width);
  const y = destRect.y + (relativeY * destRect.height);

  return { x, y };
}

// ============================================================================
// Grid Cell Utilities
// ============================================================================

/**
 * Get the cell number (1-based) for display to user
 */
export function getCellNumber(gridPos: GridPosition, divisions: number): number {
  return gridPos.cellY * divisions + gridPos.cellX + 1;
}

/**
 * Get grid position from cell number (1-based)
 */
export function cellNumberToGridPos(
  cellNumber: number,
  divisions: number
): GridPosition {
  const safeDiv = safeDivisions(divisions);
  const index = cellNumber - 1;
  const cellX = index % safeDiv;
  const cellY = Math.floor(index / safeDiv);

  return {
    cellX,
    cellY,
    offsetX: 0.5, // Center of cell
    offsetY: 0.5
  };
}

/**
 * Get the bounding box of a grid cell in source image pixels
 */
export function getCellBounds(
  cellX: number,
  cellY: number,
  gridSystem: GridSystem
): Rect {
  return {
    x: cellX * gridSystem.cellWidth,
    y: cellY * gridSystem.cellHeight,
    width: gridSystem.cellWidth,
    height: gridSystem.cellHeight
  };
}

// ============================================================================
// Validation
// ============================================================================

/**
 * Validate a grid position
 */
export function validateGridPosition(
  gridPos: GridPosition | null | undefined,
  divisions: number = DEFAULT_GRID_DIVISIONS
): boolean {
  if (!gridPos) return false;

  const { cellX, cellY, offsetX, offsetY } = gridPos;

  return (
    typeof cellX === 'number' && !isNaN(cellX) && cellX >= 0 && cellX < divisions &&
    typeof cellY === 'number' && !isNaN(cellY) && cellY >= 0 && cellY < divisions &&
    typeof offsetX === 'number' && !isNaN(offsetX) && offsetX >= 0 && offsetX <= 1 &&
    typeof offsetY === 'number' && !isNaN(offsetY) && offsetY >= 0 && offsetY <= 1
  );
}

/**
 * Get default grid position (center of image)
 */
export function getDefaultGridPosition(divisions: number = DEFAULT_GRID_DIVISIONS): GridPosition {
  const centerCell = Math.floor(safeDivisions(divisions) / 2);
  return {
    cellX: centerCell,
    cellY: centerCell,
    offsetX: 0.5,
    offsetY: 0.5
  };
}

// ============================================================================
// Debug Utilities
// ============================================================================

/**
 * Log grid position details for debugging
 */
export function logGridPosition(
  label: string,
  gridPos: GridPosition,
  gridSystem?: GridSystem
): void {
  const cellNum = getCellNumber(gridPos, gridSystem?.divisions || DEFAULT_GRID_DIVISIONS);

  logger.debug(`Grid Position [${label}]`, {
    cell: `(${gridPos.cellX}, ${gridPos.cellY})`,
    offset: `(${gridPos.offsetX.toFixed(3)}, ${gridPos.offsetY.toFixed(3)})`,
    cellNumber: cellNum,
    divisions: gridSystem?.divisions || DEFAULT_GRID_DIVISIONS
  });
}

/**
 * Compare two grid positions for equality (with tolerance for floating point)
 */
export function gridPositionsEqual(
  a: GridPosition,
  b: GridPosition,
  tolerance: number = 0.001
): boolean {
  return (
    a.cellX === b.cellX &&
    a.cellY === b.cellY &&
    Math.abs(a.offsetX - b.offsetX) < tolerance &&
    Math.abs(a.offsetY - b.offsetY) < tolerance
  );
}
